import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ApiBaseUrl from "../constants/apiUrl";
import {
  Users,
  ArrowLeft,
  Download,
  Mail,
  Phone,
  MapPin,
  Clock,
  Briefcase,
  FileText,
  Calendar,
  CheckCircle,
  AlertCircle,
  Send,
  Trash2,
  ExternalLink,
  Shield,
  MessageSquare,
  DollarSign,
} from "lucide-react";

const CareerApplicationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(null);

  // Status form state
  const [statusForm, setStatusForm] = useState({
    status: "pending",
    remarks: "",
    adminNotes: "",
    sendEmailAlert: true,
    sendSmsAlert: true,
    interviewDate: "",
    interviewTime: "",
    interviewMode: "Online (Google Meet)",
    interviewLink: "",
    interviewInstructions: "",
  });

  useEffect(() => {
    fetchApplicationDetails();
  }, [id]);

  const fetchApplicationDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const res = await fetch(`${ApiBaseUrl}/admin/career/applications/${id}`, {
        headers: {
          Authorization: `Bearer ${authData?.token || ""}`,
        },
      });

      const data = await res.json();
      if (data.success && data.data) {
        const app = data.data;
        setApplication(app);
        setStatusForm({
          status: app.status || "pending",
          remarks: "",
          adminNotes: app.adminNotes || "",
          sendEmailAlert: true,
          sendSmsAlert: true,
          interviewDate: app.interviewDetails?.scheduledDate
            ? new Date(app.interviewDetails.scheduledDate).toISOString().split("T")[0]
            : "",
          interviewTime: app.interviewDetails?.scheduledTime || "",
          interviewMode: app.interviewDetails?.mode || "Online (Google Meet)",
          interviewLink: app.interviewDetails?.locationOrLink || "",
          interviewInstructions: app.interviewDetails?.instructions || "",
        });
      } else {
        setError(data.message || "Application not found");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch application details.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCV = () => {
    if (!application) return;
    const downloadUrl = `${ApiBaseUrl}/career/application/${application.applicationId || application._id}/cv`;
    window.open(downloadUrl, "_blank");
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setUpdateSuccess(null);
    setError(null);

    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const payload = {
        status: statusForm.status,
        remarks: statusForm.remarks,
        adminNotes: statusForm.adminNotes,
        sendEmailAlert: statusForm.sendEmailAlert,
        sendSmsAlert: statusForm.sendSmsAlert,
        ...(statusForm.status === "interview_scheduled" && {
          interviewDetails: {
            scheduledDate: statusForm.interviewDate,
            scheduledTime: statusForm.interviewTime,
            mode: statusForm.interviewMode,
            locationOrLink: statusForm.interviewLink,
            instructions: statusForm.interviewInstructions,
          },
        }),
      };

      const res = await fetch(
        `${ApiBaseUrl}/admin/career/applications/${application.applicationId || id}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${authData?.token || ""}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();
      if (data.success) {
        setUpdateSuccess(data.message || "Application status updated and alerts dispatched!");
        fetchApplicationDetails();
      } else {
        setError(data.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      setError("Error updating application status");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteApplication = async () => {
    if (
      !window.confirm(
        `Are you sure you want to permanently delete this application? This will also remove the candidate's CV from server storage.`
      )
    ) {
      return;
    }

    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const res = await fetch(
        `${ApiBaseUrl}/admin/career/applications/${application.applicationId || id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authData?.token || ""}`,
          },
        }
      );

      const data = await res.json();
      if (data.success) {
        alert("Application and local CV file deleted successfully.");
        navigate("/career/applications");
      } else {
        alert(data.message || "Failed to delete application");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting application");
    }
  };

  if (loading) {
    return (
      <div className="p-16 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        <p className="mt-4 text-gray-500">Loading candidate application...</p>
      </div>
    );
  }

  if (error && !application) {
    return (
      <div className="p-8 text-center text-red-600 max-w-lg mx-auto">
        <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-80" />
        <h2 className="text-xl font-bold mb-2">Application Not Found</h2>
        <p className="text-sm text-gray-600 mb-6">{error}</p>
        <Link
          to="/career/applications"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Applications
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Link
            to="/career/applications"
            className="p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Link to="/career/applications" className="hover:text-red-600">
                Applications
              </Link>
              <span>/</span>
              <span className="text-gray-900 font-medium">{application.fullName}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              {application.fullName}
              <span className="text-xs font-mono font-normal px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg">
                {application.applicationId}
              </span>
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleDownloadCV}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-sm font-semibold shadow-sm transition"
          >
            <Download className="w-4 h-4" />
            Download Candidate CV
          </button>

          <button
            onClick={handleDeleteApplication}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-xl text-sm font-semibold transition"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {updateSuccess && (
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 shrink-0" />
          <p className="text-sm font-medium">{updateSuccess}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Applicant Overview & Details (2 Cols) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Main Info Card */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Applied Position</h2>
                <p className="text-base font-semibold text-red-600 mt-0.5">
                  {application.jobTitle}
                </p>
                {application.jobDepartment && (
                  <p className="text-xs text-gray-500">{application.jobDepartment}</p>
                )}
              </div>
              <div className="text-right">
                <span className="text-xs text-gray-400 block">Submission Date</span>
                <span className="text-xs font-semibold text-gray-800">
                  {new Date(application.createdAt).toLocaleDateString("en-US", {
                    weekday: "short",
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>

            {/* Candidate Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
              <div className="p-3.5 bg-gray-50 rounded-xl">
                <span className="text-xs text-gray-500 font-medium block">Email Address</span>
                <div className="flex items-center gap-2 font-semibold text-gray-900 mt-1">
                  <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                  <a href={`mailto:${application.email}`} className="hover:text-red-600 break-all">
                    {application.email}
                  </a>
                </div>
              </div>

              <div className="p-3.5 bg-gray-50 rounded-xl">
                <span className="text-xs text-gray-500 font-medium block">Phone / Mobile</span>
                <div className="flex items-center gap-2 font-semibold text-gray-900 mt-1">
                  <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                  <a href={`tel:${application.phone}`} className="hover:text-red-600">
                    {application.phone}
                  </a>
                </div>
              </div>

              {application.whatsapp && (
                <div className="p-3.5 bg-gray-50 rounded-xl">
                  <span className="text-xs text-gray-500 font-medium block">WhatsApp</span>
                  <div className="flex items-center gap-2 font-semibold text-gray-900 mt-1">
                    <span className="text-green-600">💬</span>
                    <span>{application.whatsapp}</span>
                  </div>
                </div>
              )}

              <div className="p-3.5 bg-gray-50 rounded-xl">
                <span className="text-xs text-gray-500 font-medium block">Current Location / City</span>
                <div className="flex items-center gap-2 font-semibold text-gray-900 mt-1">
                  <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>{application.currentCity}</span>
                </div>
              </div>

              <div className="p-3.5 bg-gray-50 rounded-xl">
                <span className="text-xs text-gray-500 font-medium block">Total Experience</span>
                <div className="flex items-center gap-2 font-semibold text-gray-900 mt-1">
                  <Briefcase className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>{application.experienceYears || "Fresh"}</span>
                </div>
              </div>

              <div className="p-3.5 bg-gray-50 rounded-xl">
                <span className="text-xs text-gray-500 font-medium block">Notice Period</span>
                <div className="flex items-center gap-2 font-semibold text-gray-900 mt-1">
                  <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>{application.noticePeriod || "Immediate"}</span>
                </div>
              </div>

              {application.currentSalary && (
                <div className="p-3.5 bg-gray-50 rounded-xl">
                  <span className="text-xs text-gray-500 font-medium block">Current Salary</span>
                  <div className="flex items-center gap-2 font-semibold text-gray-900 mt-1">
                    <DollarSign className="w-4 h-4 text-gray-400 shrink-0" />
                    <span>{application.currentSalary}</span>
                  </div>
                </div>
              )}

              {application.expectedSalary && (
                <div className="p-3.5 bg-gray-50 rounded-xl">
                  <span className="text-xs text-gray-500 font-medium block">Expected Salary</span>
                  <div className="flex items-center gap-2 font-semibold text-gray-900 mt-1">
                    <DollarSign className="w-4 h-4 text-gray-400 shrink-0" />
                    <span>{application.expectedSalary}</span>
                  </div>
                </div>
              )}

              {application.portfolioUrl && (
                <div className="sm:col-span-2 p-3.5 bg-gray-50 rounded-xl">
                  <span className="text-xs text-gray-500 font-medium block">
                    Portfolio / LinkedIn URL
                  </span>
                  <a
                    href={
                      application.portfolioUrl.startsWith("http")
                        ? application.portfolioUrl
                        : `https://${application.portfolioUrl}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-blue-600 hover:underline font-semibold mt-1 break-all"
                  >
                    <span>{application.portfolioUrl}</span>
                    <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Resume & File Storage Box */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-gray-900 pb-3 border-b border-gray-100 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-600" />
                Resume / Curriculum Vitae (CV)
              </span>
              <span className="text-xs font-medium px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full">
                Stored on VPS Disk
              </span>
            </h2>

            {application.resume && application.resume.fileName ? (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0 font-bold">
                    PDF
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 break-all">
                      {application.resume.originalName || "Candidate_Resume.pdf"}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Size:{" "}
                      {application.resume.fileSize
                        ? `${(application.resume.fileSize / (1024 * 1024)).toFixed(2)} MB`
                        : "Unknown"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDownloadCV}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold shadow-sm transition"
                  >
                    <Download className="w-4 h-4" />
                    Download File
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-400">
                No resume file attached.
              </div>
            )}
          </div>

          {/* Cover Letter / Notes */}
          {application.coverLetter && (
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <h2 className="text-lg font-bold text-gray-900 pb-3 border-b border-gray-100 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-red-600" />
                Cover Letter & Candidate Note
              </h2>
              <div className="p-4 bg-gray-50 rounded-xl text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                {application.coverLetter}
              </div>
            </div>
          )}

          {/* Status History Timeline */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-gray-900 pb-3 border-b border-gray-100 flex items-center gap-2">
              <Clock className="w-5 h-5 text-red-600" />
              Application Status History & Audit Log
            </h2>

            <div className="space-y-4 pt-2">
              {application.statusHistory && application.statusHistory.length > 0 ? (
                application.statusHistory.map((hist, idx) => (
                  <div key={idx} className="flex gap-4 items-start relative pb-4 last:pb-0">
                    <div className="w-8 h-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center shrink-0 font-bold text-xs border border-red-200">
                      {idx + 1}
                    </div>
                    <div className="flex-1 bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-900">
                          {hist.status.replace(/_/g, " ")}
                        </span>
                        <span className="text-[11px] text-gray-500">
                          {new Date(hist.changedAt).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        <strong>Updated by:</strong> {hist.changedBy || "System"}
                      </p>
                      {hist.remarks && (
                        <p className="text-xs text-gray-700 mt-1 italic">
                          "{hist.remarks}"
                        </p>
                      )}
                      <div className="flex gap-3 text-[10px] text-gray-500 mt-2">
                        <span>Email: {hist.emailSent ? "✅ Sent" : "❌ Not sent"}</span>
                        <span>SMS: {hist.smsSent ? "✅ Sent" : "❌ Not sent"}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No status updates recorded yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Status Update Panel */}
        <div className="space-y-6">
          <form
            onSubmit={handleStatusSubmit}
            className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-5 sticky top-6"
          >
            <h2 className="text-lg font-bold text-gray-900 pb-3 border-b border-gray-100 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-red-600" />
              Update Status & Notify
            </h2>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Application Status
              </label>
              <select
                value={statusForm.status}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, status: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="pending">Pending Review</option>
                <option value="under_review">Under Review</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interview_scheduled">Interview Scheduled</option>
                <option value="hired">Selected / Hired</option>
                <option value="rejected">Not Selected / Rejected</option>
              </select>
            </div>

            {/* If Interview Scheduled -> show interview inputs */}
            {statusForm.status === "interview_scheduled" && (
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 space-y-3">
                <h4 className="text-xs font-bold text-purple-900 uppercase">
                  Interview Scheduling
                </h4>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={statusForm.interviewDate}
                    onChange={(e) =>
                      setStatusForm({ ...statusForm, interviewDate: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Time (PKT)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 03:00 PM"
                    value={statusForm.interviewTime}
                    onChange={(e) =>
                      setStatusForm({ ...statusForm, interviewTime: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Interview Mode
                  </label>
                  <select
                    value={statusForm.interviewMode}
                    onChange={(e) =>
                      setStatusForm({ ...statusForm, interviewMode: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                  >
                    <option value="Online (Google Meet)">Online (Google Meet)</option>
                    <option value="Online (Zoom)">Online (Zoom)</option>
                    <option value="In-Person Office">In-Person (Lahore Office)</option>
                    <option value="Phone Call">Phone Call</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Meeting Link or Location
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. https://meet.google.com/xyz-abcd-efg"
                    value={statusForm.interviewLink}
                    onChange={(e) =>
                      setStatusForm({ ...statusForm, interviewLink: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Remarks / Notes for Candidate
              </label>
              <textarea
                rows="3"
                placeholder="Message or feedback included in the notification..."
                value={statusForm.remarks}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, remarks: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Admin Internal Notes (Private)
              </label>
              <textarea
                rows="2"
                placeholder="Internal HR evaluation notes (not visible to candidate)..."
                value={statusForm.adminNotes}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, adminNotes: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
              />
            </div>

            {/* Notification alert checkboxes */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
              <h4 className="text-xs font-bold text-gray-700 uppercase mb-1">
                Trigger Alerts
              </h4>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={statusForm.sendEmailAlert}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, sendEmailAlert: e.target.checked })
                  }
                  className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                />
                <span className="text-xs font-semibold text-gray-800">
                  Send Email Alert to Candidate
                </span>
              </label>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={statusForm.sendSmsAlert}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, sendSmsAlert: e.target.checked })
                  }
                  className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                />
                <span className="text-xs font-semibold text-gray-800">
                  Send SMS Alert to Candidate
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={updating}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold shadow-md transition disabled:opacity-50"
            >
              {updating ? (
                "Updating & Sending..."
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Update Status & Send Alerts
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CareerApplicationDetails;
